import { ComponentProps, Dispatch, SetStateAction } from 'react';
import { components } from 'react-select';
import debounce from 'lodash.debounce';

import SingleAutocomplete from '~/shared/components/autocomplete/SingleAutocomplete';
import { GroupLogSelectionOption, LogSelectionOption } from '~/shared/utils/types';
import { useXFetch } from '~/shared/utils/useXFetch';
import { VolumesAutoCompleteResponse } from '~/shared/types/api';

export default function SearchFilter({
  setFilters,
}: {
  setFilters: Dispatch<SetStateAction<LogSelectionOption[]>>;
}) {
  const api = useXFetch();

  const _loadOptions: NonNullable<ComponentProps<typeof SingleAutocomplete>['loadOptions']> = (
    pattern,
    callback
  ) => {
    function get(field: 'volumeId' | 'instanceId') {
      const qs = new URLSearchParams({
        field,
        pattern,
      }).toString();
      if (!pattern) {
        return [];
      }
      return api<VolumesAutoCompleteResponse>(`/api/v1/volumes/autocomplete?${qs}`)
        .then((d) => d.values || [])
        .catch((e) => {
          console.error(e);
          return [];
        });
    }

    const doIt = async function () {
      const [instanceIds, volumeIds] = await Promise.all([get('instanceId'), get('volumeId')]);
      const data: GroupLogSelectionOption[] = [
        {
          label: 'Volume ID',
          options: volumeIds?.map((id) => ({ id, name: id, group: 'volumeId' })),
        },
        {
          label: 'Instance ID',
          options: instanceIds?.map((id) => ({ id, name: id, group: 'instanceId' })),
        },
      ];
      return data;
    };

    // TODO: debounce failed with promise, so using callback and this weird call
    doIt().then((data) => {
      console.log({ data });
      callback(data as GroupLogSelectionOption[]);
    });
  };

  const loadOptions = debounce(_loadOptions, 200);

  return (
    <SingleAutocomplete
      onChange={(e) =>
        setFilters((prev) =>
          prev.some((f) => f.id === e?.id) ? [...prev] : [...prev, e as LogSelectionOption]
        )
      }
      loadOptions={loadOptions}
      customMenu={(menuProps) => (
        <components.Menu className="p-[4px]" {...menuProps}>
          {menuProps?.children}
        </components.Menu>
      )}
      mustTypeFirst={true}
      mustTypeFirstMessage="Start typing instance or volume id"
      className="w-[30.55%] min-w-[30.55%]"
      placeholder="Search"
    />
  );
}
